import { DateTime } from 'luxon';

export interface AllDayEventOptions {
    prodId: string;
    uid: string;
    date: DateTime;
    title: string;
    description: string;
    stamp?: DateTime;
}

// One all-day iCalendar (RFC 5545) event as file text: CRLF line endings,
// text values escaped, lines folded at 75 octets.
export function AllDayEvent(options: AllDayEventOptions): string {
    const start = options.date.startOf('day');
    const stamp = (options.stamp ?? DateTime.utc()).toUTC();
    const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        `PRODID:${options.prodId}`,
        'BEGIN:VEVENT',
        `UID:${options.uid}`,
        `DTSTAMP:${stamp.toFormat("yyyyMMdd'T'HHmmss'Z'")}`,
        `DTSTART;VALUE=DATE:${start.toFormat('yyyyMMdd')}`,
        `DTEND;VALUE=DATE:${start.plus({ days: 1 }).toFormat('yyyyMMdd')}`,
        `SUMMARY:${escapeText(options.title)}`,
        `DESCRIPTION:${escapeText(options.description)}`,
        'END:VEVENT',
        'END:VCALENDAR',
    ];
    return `${lines.map(foldLine).join('\r\n')}\r\n`;
}

function escapeText(value: string): string {
    return value.replace(/\\/g, '\\\\').replace(/\r\n|\n|\r/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
}

// Folds by UTF-8 octets, never splitting a character: continuation lines start
// with one space, which counts toward their 75.
function foldLine(line: string): string {
    const parts: string[] = [];
    let current = '';
    let size = 0;
    for (const char of line) {
        const bytes = utf8Length(char);
        const limit = parts.length === 0 ? 75 : 74;
        if (size + bytes > limit) {
            parts.push(current);
            current = '';
            size = 0;
        }
        current += char;
        size += bytes;
    }
    parts.push(current);
    return parts.join('\r\n ');
}

function utf8Length(char: string): number {
    const code = char.codePointAt(0) ?? 0;
    if (code < 0x80) {
        return 1;
    }
    if (code < 0x800) {
        return 2;
    }
    return code < 0x10000 ? 3 : 4;
}
